import { useAuthStore } from '../stores/auth';
import { toast } from '@/hooks/use-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v4';

/**
 * Error thrown for non-OK API responses. Carries the HTTP status so callers
 * handling errors inline (silent mode) can branch on 404/410/etc.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions extends RequestInit {
  /** Suppress the global error toast on failure (for non-critical loads) */
  silent?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { silent, ...fetchOptions } = options;
  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
  });

  if (!res.ok) {
    if (res.status === 401 && !silent) {
      // Try silent token refresh before logging out
      const stillValid = await useAuthStore.getState().validateSession();
      if (stillValid) {
        // Retry the original request with the refreshed token
        const retryToken = useAuthStore.getState().token;
        if (retryToken) {
          headers['Authorization'] = `Bearer ${retryToken}`;
          const retryRes = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });
          if (retryRes.ok) return retryRes.json();
        }
      }
      useAuthStore.getState().logout();
      toast({
        variant: 'destructive',
        title: 'Session expired',
        description: 'Please sign in again.',
      });
      // Remember where they were. Without this, signing back in dumps them on
      // the races list having lost whatever they were in the middle of.
      const here = window.location.pathname + window.location.search;
      const onAuthPage = /^\/(login|signup|auth\/verify)/.test(window.location.pathname);
      window.location.href = onAuthPage
        ? '/login'
        : `/login?redirect=${encodeURIComponent(here)}`;
      throw new Error('Unauthorized');
    }

    // Try to extract a meaningful error message from the response
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body.error) {
        // API returns { error: { code, message, details? } } or { error: "string" }
        message = typeof body.error === 'string'
          ? body.error
          : body.error.message || body.error.code || JSON.stringify(body.error);
        // Validation errors carry the actual reasons in a details array
        // (e.g. publish: "Race artifacts and analytics are still processing").
        // Without them the toast just says "validation failed".
        const details = typeof body.error === 'object' ? body.error.details : undefined;
        if (Array.isArray(details) && details.length > 0) {
          message = `${message}: ${details.filter((d) => typeof d === 'string').join('; ')}`;
        }
      } else if (body.message) {
        message = body.message;
      }
    } catch {
      // Response wasn't JSON — use status text
    }

    if (!silent) {
      toast({
        variant: 'destructive',
        title: 'Request failed',
        description: message,
      });
    }

    throw new ApiError(res.status, message);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string, options?: { silent?: boolean }) =>
    request<T>(path, options),
  post: <T>(path: string, body: unknown, options?: { silent?: boolean }) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body), ...options }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
